import type { AssetFileDoc, AssetFolderDoc } from '@bb/domain/dist/schemas';
import { Color } from '@bb/elements-web';
import { Card, Icon, Image, ListItem, Tooltip } from 'components';
import React from 'react';
import styled from 'styled-components';
import { mimeTypeToIcon } from 'utils';

type AssetGridItemProps = {
  item: AssetFolderDoc | AssetFileDoc;
  actionsEnabled: boolean;
  onAssetClick: (
    asset: AssetFolderDoc | AssetFileDoc,
    filetype: string,
  ) => void;
  onActionClick: (asset: AssetFolderDoc | AssetFileDoc) => void;
};

const AssetGridItem: React.FC<AssetGridItemProps> = ({
  item,
  actionsEnabled,
  onAssetClick,
  onActionClick,
}) => {
  const filetype = (item as AssetFileDoc).filetype || 'folder';

  const handleClick = () => {
    onAssetClick(item, filetype);
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onActionClick(item);
  };

  return (
    <StyledListItem data-cy={`asset-item-${item.id}`}>
      <StyledCard onClick={handleClick} hoverable>
        {actionsEnabled && (
          <StyledActionsContainer
            data-cy="asset-action-button"
            onClick={handleActionClick}
          >
            <Icon
              type={
                filetype === 'folder'
                  ? 'antdSettingOutlined'
                  : 'antdDeleteFilled'
              }
              width={16}
            />
          </StyledActionsContainer>
        )}
        <StyledCardContent>
          {filetype !== 'folder' && (item as AssetFileDoc).thumbnail ? (
            <StyledImgPreview
              preview={false}
              src={(item as AssetFileDoc).thumbnail}
              alt={item.name}
            />
          ) : (
            <Icon type={mimeTypeToIcon(filetype)} width={64} height={64} />
          )}
        </StyledCardContent>
        <StyledCardFooter>
          <Tooltip title={item.name}>
            <StyledCardFooterText>{item.name}</StyledCardFooterText>
          </Tooltip>
        </StyledCardFooter>
      </StyledCard>
    </StyledListItem>
  );
};

export default React.memo(AssetGridItem);

const StyledListItem = styled(ListItem)`
  &&& {
    padding: 0;
    margin-bottom: 5px;
  }
`;

const StyledCard = styled(Card)`
  &&& {
    border: 1px solid ${Color.BEERUS};
  }

  && .ant-card-body {
    cursor: pointer;
    position: relative;
    height: 225px;
    display: flex;
    flex-direction: column;
  }
`;

const StyledActionsContainer = styled.div`
  cursor: pointer;
  background: ${Color.LIGHTER_GREY};
  position: absolute;
  top: 0px;
  right: 0px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const StyledCardContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;

const StyledCardFooter = styled.div`
  margin-top: auto;
  width: 100%;
  height: auto;
  min-height: 40px;
  background: ${Color.LIGHTER_GREY};
  border-top: 1px solid ${Color.BEERUS};
  padding: 10px 15px;
  font-size: 90%;
`;

const StyledCardFooterText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledImgPreview = styled(Image)`
  max-height: 170px;
`;
